import type { BlockType } from '@unifyapps/defs/types/block';
import type { DependsOnType } from '@unifyapps/defs/types/page';
import { DependencyHelper } from '../../dependency/helpers/DependencyHelper';
import { BlockHelper } from '../../helpers/BlockHelper';
import type BlocksRegistry from '../context/RegistryProvider/BlocksRegistry';
import { DependencyPaths } from '../helper/DependencyPaths';
import { ComputeContextKeys } from '../hooks/computeContext/const';
import { getDataSourceResponsesDeps } from '../hooks/useDataSourceResponses';
import { getIsEntitySuspendedDependencies } from '../hooks/useIsEntitySuspended';
import { ENTIRE_ENTITY } from './const';

export const getPageFunctionDeps = (pageFunctionIds: string[]) => {
  const pageFunctionDeps = pageFunctionIds.map((pageFunctionId) => {
    const paths = DependencyPaths.pageFunction(pageFunctionId);
    return {
      id: pageFunctionId,
      p: [paths.isEvaluated],
    };
  });

  return DependencyHelper.mergeDependsOn([pageFunctionDeps]);
};

function getDepsFromBlockImplicitDepsGroups({
  pageBlock,
  registry,
}: {
  pageBlock: BlockType;
  registry: BlocksRegistry;
}) {
  const dependencyGroups = registry.getBlockImplicitDependencyGroups(
    pageBlock.component.componentType,
  );

  const deps: DependsOnType[][] = [];

  if (dependencyGroups?.isEntitySuspended) {
    const suspendedGrpDeps = getIsEntitySuspendedDependencies(
      BlockHelper.getDataSourceIds(pageBlock),
    );
    deps.push(suspendedGrpDeps);
  }

  if (dependencyGroups?.usingUserContext) {
    deps.push([
      {
        id: ComputeContextKeys.USER_CONTEXT,
        p: [ENTIRE_ENTITY],
      },
    ]);
  }

  if (dependencyGroups?.usingDataSourcesResponse) {
    const dataSourcesResponse = getDataSourceResponsesDeps(BlockHelper.getDataSourceIds(pageBlock));
    deps.push(dataSourcesResponse);
  }

  if (dependencyGroups?.usingPageFunctionLoaders) {
    const pageFunctionIds = BlockHelper.getPageFunctionIds(pageBlock);
    const pageFunctionDeps = getPageFunctionDeps(pageFunctionIds);
    deps.push(pageFunctionDeps);
  }

  return DependencyHelper.mergeDependsOn(deps);
}

export function buildBlockImplicitDependencies({
  block,
  registry,
  interfacePageId,
}: {
  block: BlockType;
  interfacePageId: string;
  registry: BlocksRegistry;
}) {
  // first get the implicit deps added by blocks,
  // these are things from the context that the block needs
  const blockImplicitDeps =
    registry.getBlockImplicitDependsOn({
      name: block.component.componentType,
      block,
      interfacePageId,
    }) ?? [];

  // these deps are standard, since block must be using that hook/feature,
  // it has marked the associated dep group required
  const blockDepsFromImplicitDepsGroups = getDepsFromBlockImplicitDepsGroups({
    pageBlock: block,
    registry,
  });

  return DependencyHelper.mergeDependsOn([blockImplicitDeps, blockDepsFromImplicitDepsGroups]);
}
